import { Injectable } from '@angular/core';
import { ApiClientHelper } from '../../helpers/api-client.helper';
import { StringHelper } from '../../helpers/strings.helper';
import { LocalStorageService } from '../../services/local-storage.service';
import { AppConfig } from '../../../shared/config/app-config';
import { UserAlias } from '../../../shared/constants';
import { StorageKeys } from '../../../shared/models/storage-keys';
import { BehaviorSubject } from 'rxjs';

@Injectable({
	providedIn: 'root'
})
export class StaffManagementService {

	private baseUrl = `https://jsonplaceholder.typicode.com/`;
	private designationUrl = 'users';
	public saveSubscription = new BehaviorSubject<boolean>(false);
	public staffBusinesses = [];
	public tokenDataDeviceResponse;

	constructor(
		private apiClientHelper: ApiClientHelper,
		private localStorage: LocalStorageService,
		public config: AppConfig,
	) {

	}

	getDesignations() {
		return this.apiClientHelper.getForObservable(`${this.baseUrl}${this.designationUrl}`);
	}

	saveDesignation(data: { permissions: any[]; name: any; description: any }) {
		return this.apiClientHelper.postForObservable<any>(`${this.baseUrl}${this.designationUrl}`, data);
	}

	addBusinessToStaffUser(id, params): Promise<any> {
		const url = `${this.config.MONIEPOINT_HOST}/api/v1/staff-user/${id}/business`;
		return this.apiClientHelper.putForPromise(url, params);
	}

	deleteBusinessFromStaffUser(staffId, businessId): Promise<any> {
		const url = `${this.config.MONIEPOINT_HOST}/api/v1/staff-user/${staffId}/business/${businessId}`;
		return this.apiClientHelper.delete(url, true);
	}

	updateBusinessDesignation(id, params): Promise<any> {
		const url = `${this.config.MONIEPOINT_HOST}/api/v1/staff-user/${id}/business`;
		return this.apiClientHelper.putForPromise(url, params);
	}

	getStaffListBO(params, status = 'active'): Promise<any> {
		const urlString = StringHelper.toUrlString(params);
		const url = `${this.config.MONIEPOINT_HOST}/api/v1/staff-user/business-owners?${urlString}`;
		return this.apiClientHelper.getForPromise(url);
	}

	getStaffListBU(params, status = 'active'): Promise<any> {
		const urlString = StringHelper.toUrlString(params);
		const url = `${this.config.MONIEPOINT_HOST}/api/v1/staff-user/business?${urlString}`;
		return this.apiClientHelper.getForPromise(url);
	}

	inviteNewStaffUser(params): Promise<any> {
		const url = `${this.config.MONIEPOINT_HOST}/api/v1/staff-user`;
		return this.apiClientHelper.postForPromise(url, params);
	}

	resendInviteEmail(staffId): Promise<any> {
		const url = `${this.config.MONIEPOINT_HOST}/api/v1/staff-user/${staffId}/activation-link`;
		return this.apiClientHelper.postForPromise(url, {});
	}

	deleteStaffUser(staffId) {
		const url = this.config.MONIEPOINT_HOST + `/api/v1/staff-user/${staffId}/de-activate`;
		return this.apiClientHelper.delete(url, true);
	}

	suspendStaffUser(staffId) {
		const url = this.config.MONIEPOINT_HOST + `/api/v1/staff-user/${staffId}/suspend`;
		return this.apiClientHelper.delete(url, true);
	}

	unSuspendStaffUser(staffId) {
		const url = this.config.MONIEPOINT_HOST + `/api/v1/staff-user/${staffId}/un-suspend`;
		return this.apiClientHelper.postForPromise(url, {});
	}

	getStaffUserRoles(params) {
		const urlString = StringHelper.toUrlString(params);
		const url = `${this.config.MONIEPOINT_HOST}/api/v1/staff-user-roles?${urlString}`;
		return this.apiClientHelper.getForPromise(url);
	}

	getBusinesses(params) {
		const urlString = StringHelper.toUrlString(params);
		const url = `${this.config.MONIEPOINT_DASHBOARD}/api/v2/businesses?${urlString}`;
		return this.apiClientHelper.getForPromise(url);
	}

	getStaffBusinesses(staffId) {
		const url = `${this.config.MONIEPOINT_HOST}/api/v1/staff-user/${staffId}/businesses`;
		return this.apiClientHelper.getForPromise(url);
	}

	updateStaffUser(staffId, params) {
		const url = `${this.config.MONIEPOINT_HOST}/api/v1/staff-user/${staffId}`;
		return this.apiClientHelper.putForPromise(url, params);
	}

	getApprovalRequests(params) {
		const url = `${this.config.MONIEPOINT_HOST}/api/v1/staff-user/transaction/requests`;
		return this.apiClientHelper.getForPromise(url);
	}

	async switchStaffBusiness(businessId: number) {
		const refreshToken = JSON.parse(this.localStorage.get(StorageKeys.STAFF_USER_REFRESH_TOKEN));
		const url = `${this.config.MONIEPOINT_USER_URL}/api/v1/auth/staff-user/token`;
		try {
			const res = await this.apiClientHelper.postForPromise(url, { businessId, refreshToken });
			return res;
		} catch (error) {
			throw error;
		}
	}

	isStaffUserAdmin() {
		const roles = JSON.parse(this.localStorage.get(StorageKeys.roles)) || [];
		return roles.includes(UserAlias.staffAdmin);
	}

	isStaffSuperAuditor() {
		const roles = JSON.parse(this.localStorage.get(StorageKeys.roles)) || [];
		return roles.includes(UserAlias.staffSuperAuditor);
	}

	getStaffUserLinkedBusinesses() {
		const url = `${this.config.MONIEPOINT_USER_URL}/api/v1/staff-user/linked-businesses`;
		return this.apiClientHelper.getForPromise(url);
  }

  getUserType() {
    const url = `${this.config.MONIEPOINT_OFFLINE_SALES_URL}/partner-management/api/v1/partners/usertype-info`;
		return this.apiClientHelper.getForPromise(url);
  }

  getKioskMarketerOnboardingStage(): Promise<any> {
    const url = `${this.config.MONIEPOINT_OFFLINE_SALES_URL}/partner-onboarding/api/v1/partners/self-onboarding/progress`;
    return this.apiClientHelper.getForPromise(url);
  }
}
